<template>
  <v-navigation-drawer
    class="app-sidebar-drawer overflow-visible"
    :model-value="modelValue"
    :data-locator="dataLocator"
    :order="order ?? 1"
    :permanent="permanent"
    :rail="mini"
    :width="resolvedWidth"
    :rail-width="railWidth"
    v-bind="$attrs"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="app-sidebar">
      <div v-if="shouldDisplayHomeItem || $slots['top']" class="app-sidebar__top">
        <div v-if="shouldDisplayHomeItem" class="app-sidebar__home">
          <AppSidebarItem
            :item="homeSidebarItem"
            :icon-only="mini"
            @select="goToHome"
          />
          <v-divider class="app-sidebar__home-divider" style="opacity: 1;" color="border-primary" />
        </div>

        <slot name="top" />
      </div>

      <div class="app-sidebar__content">
        <slot />
      </div>

      <div
        class="app-sidebar__footer d-flex align-center"
        :class="[
          mini ? 'column-gap-1 justify-center w-100' : 'column-gap-3 pl-5',
          hasSubscriptionLogo ? '' : 'mb-2',
        ]"
      >
        <div class="d-flex align-center column-gap-2">
          <app-material-icon name="info" size="sm" />
          <span v-if="!mini" class="text-grey-500" data-locator="closd-version">
            V{{ closdVersion }}
          </span>
        </div>

        <div v-if="hasSubscriptionLogo" class="d-flex align-center my-2">
          <v-img
            alt="Closd Name"
            style="vertical-align: middle"
            :src="computedClosdLogoFooter"
            :width="logoWidthFooter"
            data-locator="closd-logo-sidebar-footer"
          />
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'

import closdLogo from '@/assets/img/closd_logo.svg'
import closdLogoMiniFooter from '@/assets/img/closd_logo_mini_footer.svg'
import type { AppSidebarItemData } from '@/common/app-sidebar/appSidebar.types'
import AppSidebarItem from '@/common/app-sidebar/AppSidebarItem.vue'
import { PROJECT_NAV_DRAWER_WIDTHS } from '@/common/layout/constants'
import { ROOMS_ROUTE_NAME } from '@/router/routerData'

export default defineComponent({
  name: 'AppSidebar',
  components: { AppSidebarItem },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    width: {
      type: [Number, String] as PropType<number | string>,
      default: undefined,
    },
    railWidth: {
      type: [Number, String] as PropType<number | string>,
      default: PROJECT_NAV_DRAWER_WIDTHS.RAW.FOLDED,
    },
    mini: {
      type: Boolean,
      default: false,
    },
    permanent: {
      type: Boolean,
      default: false,
    },
    order: {
      type: [Number, String] as PropType<number | string>,
      default: undefined,
    },
    dataLocator: {
      type: String,
      default: 'app-navigation',
    },
  },
  emits: ['update:modelValue'],
  computed: {
    ...mapState('global', ['homeLayout']),
    ...mapGetters('subscription', ['getSubscriptionLogo']),
    shouldDisplayHomeItem (): boolean {
      return !this.homeLayout
    },
    homeSidebarItem (): AppSidebarItemData {
      return {
        key: ROOMS_ROUTE_NAME,
        label: this.$t('project.ProjectSidebar.home').toString(),
        icon: 'home',
        kind: 'nav',
        active: this.$route.name === ROOMS_ROUTE_NAME,
        dataLocator: 'home-nav-item',
      }
    },
    resolvedWidth (): number | string {
      return this.width ?? PROJECT_NAV_DRAWER_WIDTHS.RAW.REGULAR
    },
    hasSubscriptionLogo (): boolean {
      return !!this.getSubscriptionLogo
    },
    computedClosdLogoFooter (): string {
      return this.mini ? closdLogoMiniFooter : closdLogo
    },
    logoWidthFooter (): string {
      return this.mini ? '22px' : '67px'
    },
    closdVersion (): string {
      return __APP_VERSION__
    },
  },
  methods: {
    goToHome () {
      if (this.$route.name !== ROOMS_ROUTE_NAME) {
        this.$router.push({ name: ROOMS_ROUTE_NAME })
      }

      if (this.$vuetify.display.mobile) {
        this.$emit('update:modelValue', false)
      }
    },
  },
})
</script>

<style scoped lang="scss">
:deep(.v-navigation-drawer__content) {
  overflow: hidden;
}

.app-sidebar-drawer {
  border-right: 1px solid rgb(var(--v-theme-border-primary));
}

.app-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__top {
    flex: 0 0 auto;
  }

  &__home {
    padding: 8px;
    padding-bottom: 0;
  }

  &__home-divider {
    margin-top: 8px;
  }

  &__content {
    flex: 1 1 auto;
    min-height: 0;
  }

  &__footer {
    flex: 0 0 auto;
    min-height: 44px;
    padding-bottom: 80px;
  }
}

:deep(.app-sidebar-menu) {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
}

:deep(.app-sidebar-menu--sections) {
  gap: 8px;
}

:deep(.app-sidebar-menu__section),
:deep(.app-sidebar-menu__items) {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

:deep(.app-sidebar-menu > .app-sidebar-item--section:not(:first-child)) {
  margin-top: 8px;
}

@media (max-width: 960px) {
  .app-sidebar-drawer {
    border-right: none;
  }
}
</style>
